import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import type {
  League,
  LeagueTeamEntry,
  TeamDetail,
  GameweekTeamDetail,
  GameweekLeaderboardEntry,
  Pagination,
} from "@/types/api.types";

export interface LeagueFilters {
  search?: string;
  page?: number;
  limit?: number;
}

export const leagueService = {
  getAll: (
    filters?: LeagueFilters,
  ): Promise<{ leagues: League[]; pagination: Pagination }> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    const query = params.toString();
    return apiClient.get(
      `${API_ENDPOINTS.LEAGUES.LIST}${query ? `?${query}` : ""}`,
    );
  },

  getOne: (id: number): Promise<League> =>
    apiClient.get(API_ENDPOINTS.LEAGUES.DETAIL(id)),

  getTeams: (id: number): Promise<{ teams: LeagueTeamEntry[]; hasCompletedGameweeks: boolean }> =>
    apiClient.get(API_ENDPOINTS.LEAGUES.TEAMS(id)),

  getTeamPlayers: (
    teamId: number,
    gameweekId?: number,
  ): Promise<TeamDetail | GameweekTeamDetail> => {
    const url = API_ENDPOINTS.LEAGUES.TEAM_PLAYERS(teamId);
    return apiClient.get(
      gameweekId ? `${url}?gameweekId=${gameweekId}` : url,
    );
  },

  getLeagueGameweekLeaderboard: (
    leagueId: number,
    gameweekId: number,
  ): Promise<GameweekLeaderboardEntry[]> =>
    apiClient.get(
      API_ENDPOINTS.GAMEWEEKS.LEAGUE_LEADERBOARD(gameweekId, leagueId),
    ),

  create: (data: {
    name: string;
    isPrivate?: boolean;
    maxMembers?: number;
  }): Promise<League> => apiClient.post(API_ENDPOINTS.LEAGUES.CREATE, data),

  update: (
    id: number,
    data: { name?: string; isPrivate?: boolean; maxMembers?: number },
  ): Promise<League> => apiClient.patch(API_ENDPOINTS.LEAGUES.UPDATE(id), data),

  delete: (id: number): Promise<{ message: string }> =>
    apiClient.delete(API_ENDPOINTS.LEAGUES.DELETE(id)),
};
